import React, { useEffect, useMemo, useState } from "react";
import { Building2, Plus, GitMerge, X, SearchX, Users, UserCheck, UserX, Settings, Search, BarChart3, Briefcase, Filter } from "lucide-react";
import StatsGrid from "../../components/StatsGrid";
import { getResponsiveStyles } from "../../utils/styleUtils";
import AlertModal from "../../components/AlertModal";
import AccessDenied from "../../components/AccessDenied";
import SearchBar from "../../components/SearchBar";
import ReusableDropdown from "../../components/ReusableDropdown";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";
import { useDepartmentsAdmin, ALL_COMPANIES } from "./hooks/useDepartmentsAdmin";
import getDepartmentsStyles, { getDepartmentsGlobalCss } from "./styles/departmentsStyles";
import DepartmentsTable from "./components/DepartmentsTable";
import DepartmentFormModal from "./components/modals/DepartmentFormModal";
import MergeModal from "./components/modals/MergeModal";
import MembersModal from "./components/modals/MembersModal";
import ManagersModal from "./components/modals/ManagersModal";
import DeactivateModal from "./components/modals/DeactivateModal";

const getScreenSize = () => {
  const width = window.innerWidth;
  return { width, isMobile: width <= 768, isTablet: width > 768 && width <= 1024 };
};

// Case-insensitive and forgiving of common Arabic spelling variants
// (أحمد = احمد, المالية = الماليه, مستشفى = مستشفي) and diacritics.
const normalizeSearch = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[\u064B-\u0652\u0640]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/\s+/g, " ")
    .trim();

function DepartmentsPage() {
  const {
    theme, language, t, profileLoading, can,
    companies, selectedCompany, setSelectedCompany,
    departments, loading, showInactive, setShowInactive,
    alertModal, setAlertModal,
    createDepartment, updateDepartment, deactivateDepartment,
    mergeDepartments, setManagers, fetchMembers,
  } = useDepartmentsAdmin();

  const isRTL = language === "ar";
  const [screenSize, setScreenSize] = useState(getScreenSize);
  useEffect(() => {
    const onResize = () => setScreenSize(getScreenSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const styles = useMemo(() => getDepartmentsStyles(screenSize, theme, language), [screenSize, theme, language]);
  const globalCss = useMemo(() => getDepartmentsGlobalCss(theme), [theme]);
  // Shared stat-card look (same source as Employee Management's StatsGrid).
  const sharedStyles = useMemo(
    () => getResponsiveStyles({ ...screenSize, isDesktop: !screenSize.isMobile && !screenSize.isTablet }, theme, language),
    [screenSize, theme, language]
  );

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editModal, setEditModal] = useState(null);   // null | {mode:'create'} | {mode:'edit', dept}
  const [mergeModal, setMergeModal] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [membersModal, setMembersModal] = useState(null);  // {dept, members, loading}
  const [managersModal, setManagersModal] = useState(null); // {dept, list, suggestion}
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const words = normalizeSearch(search).split(" ").filter(Boolean);
    if (!words.length) return departments;
    return departments.filter((d) => {
      const haystack = normalizeSearch(
        [
          d.name,
          d.nameAr,
          d.description,
          ...(d.managers || []).flatMap((m) => [m.employee?.sr, m.employee?.arabicName, m.infinityCode]),
        ].filter(Boolean).join(" ")
      );
      // Every typed word must appear somewhere (name, Arabic name, description or a manager).
      return words.every((word) => haystack.includes(word));
    });
  }, [departments, search]);

  const stats = useMemo(() => {
    const withManagers = departments.filter((d) => (d.managers || []).length > 0).length;
    return {
      total: departments.length,
      members: departments.reduce((sum, d) => sum + (d._count?.employees ?? 0), 0),
      withManagers,
      withoutManagers: departments.length - withManagers,
    };
  }, [departments]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (err) {
      setAlertModal({ open: true, title: t.error, message: err.message, variant: "error" });
    } finally { setBusy(false); }
  };

  const openMembers = async (dept) => {
    setMembersModal({ dept, members: [], loading: true });
    try {
      const members = await fetchMembers(dept.id);
      setMembersModal({ dept, members, loading: false });
    } catch (err) {
      setMembersModal(null);
      setAlertModal({ open: true, title: t.error, message: err.message, variant: "error" });
    }
  };

  const openManagers = async (dept) => {
    let suggestion = null;
    let members = [];
    try { members = await fetchMembers(dept.id); } catch { /* suggestion is optional */ }
    const votes = new Map();
    for (const m of members) {
      if (!m.firstLineManagerInfinityCode) continue;
      const k = m.firstLineManagerInfinityCode;
      votes.set(k, { code: k, name: m.firstLineManagerName, n: (votes.get(k)?.n || 0) + 1 });
    }
    for (const v of votes.values()) if (!suggestion || v.n > suggestion.n) suggestion = v;
    setManagersModal({
      dept,
      list: (dept.managers || []).map((m) => ({ infinityCode: m.infinityCode, role: m.role })),
      members,
      suggestion,
      newCode: "",
    });
  };

  const renderSkeletonRows = () => (
    <div style={styles.tableWrap}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: "24px", padding: "16px", borderBottom: `1px solid ${styles.colors.rowBorder}` }}>
          <div style={{ flex: 2 }}>
            <SkeletonBox width="55%" height="14px" style={{ marginBottom: "6px" }} />
            <SkeletonBox width="35%" height="12px" />
          </div>
          <SkeletonBox width="90px" height="24px" style={{ borderRadius: "999px" }} />
          <div style={{ flex: 2 }}><SkeletonBox width="60%" height="24px" style={{ borderRadius: "999px" }} /></div>
          <SkeletonBox width="70px" height="24px" style={{ borderRadius: "999px" }} />
          <SkeletonBox width="90px" height="28px" />
        </div>
      ))}
    </div>
  );

  // ── gate ──
  if (profileLoading) {
    return (
      <div className="nx-flush-mobile" style={styles.container}>
        <SkeletonStyles theme={theme} />
        <div className="nx-page-header" style={{ ...styles.header, animation: "none" }}>
          <div style={styles.headerText}>
            <LoadingPill text={t.loading} theme={theme} isRTL={isRTL} />
            <SkeletonBox width="220px" height="30px" style={{ marginBottom: "12px" }} />
            <SkeletonBox width="320px" height="16px" style={{ maxWidth: "100%" }} />
          </div>
        </div>
        <div style={styles.statsGrid}>
          {[1, 2, 3, 4].map((i) => <SkeletonStatCard key={i} cardStyle={{ ...sharedStyles.statCard, marginBottom: 0 }} />)}
        </div>
        <div style={styles.card}>{renderSkeletonRows()}</div>
      </div>
    );
  }
  if (!can.view) {
    return <AccessDenied pageLabel={t.title} />;
  }

  const selectedCount = selectedIds.size;
  const isAllCompanies = selectedCompany === ALL_COMPANIES;
  // Merging is only valid inside one company (the API rejects mixed sets).
  const selectedCompanies = new Set(
    departments.filter((d) => selectedIds.has(d.id)).map((d) => d.workingCompany)
  );
  const mixedCompanySelection = selectedCompanies.size > 1;
  const canMerge = selectedCount >= 2 && !mixedCompanySelection;
  const hasSearch = !!search.trim();

  return (
    <div className="dept-scope nx-flush-mobile" style={styles.container}>
      <style>{globalCss}</style>
      <SkeletonStyles theme={theme} />

      {/* ── Header ── */}
      <div className="nx-page-header" style={styles.header}>
        <div style={styles.headerText}>
          <h1 className="nx-page-title" style={styles.title}>
            <span style={styles.titleIcon}><Building2 size={screenSize.isMobile ? 24 : 28} /></span>
            {t.title}
          </h1>
          <p style={styles.subtitle}>{t.subtitle}</p>
        </div>
        <div style={styles.headerActions}>
          {can.merge && (
            <button
              type="button"
              style={{ ...styles.btnSecondary(!canMerge), flex: screenSize.isMobile ? 1 : "none" }}
              disabled={!canMerge}
              title={mixedCompanySelection ? t.mergeSameCompany : t.mergeSelectHint}
              onClick={() => { setMergeTargetId(String([...selectedIds][0])); setMergeModal(true); }}
            >
              <GitMerge size={16} />{t.merge}{selectedCount >= 2 ? ` (${selectedCount})` : ""}
            </button>
          )}
          {can.add && (
            <button
              type="button"
              className="dept-btn-primary"
              style={{ ...styles.btnPrimary(), flex: screenSize.isMobile ? 1 : "none" }}
              onClick={() => setEditModal({
                mode: "create", name: "", nameAr: "", description: "",
                workingCompany: isAllCompanies ? "" : selectedCompany,
              })}
            >
              <Plus size={16} />{t.newDepartment}
            </button>
          )}
        </div>
      </div>

      {/* ── Stats ── */}
      {selectedCompany && (
        <div style={styles.statsGrid}>
          {loading && !departments.length ? (
            [1, 2, 3, 4].map((i) => <SkeletonStatCard key={i} cardStyle={{ ...sharedStyles.statCard, marginBottom: 0 }} />)
          ) : (
            <StatsGrid
              styles={{ ...sharedStyles, container: { direction: isRTL ? "rtl" : "ltr" } }}
              screenSize={screenSize}
              layout="inline"
              gridStyle={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", marginBottom: 0 }}
              stats={[
                { title: t.statTotal, value: stats.total, icon: Building2, iconBackground: "rgb(46, 124, 186)" },
                { title: t.statMembers, value: stats.members, icon: Users, iconBackground: "rgb(0, 180, 72)" },
                { title: t.statWithManagers, value: stats.withManagers, icon: UserCheck, iconBackground: "rgb(6, 0, 180)" },
                { title: t.statWithoutManagers, value: stats.withoutManagers, icon: UserX, iconBackground: "rgb(180, 57, 0)" },
              ]}
            />
          )}
        </div>
      )}

      {/* ── Search & Filter ── */}
      <div style={styles.sectionCard}>
        <div style={styles.sectionHeader}>
          <div>
            <h3 style={styles.sectionTitle}>
              <Settings size={screenSize.isMobile ? 16 : 20} />
              {t.searchAndFilter}
            </h3>
            <p style={styles.sectionSubtitle}>{t.findAndFilter}</p>
          </div>
          {selectedCompany && departments.length > 0 && (
            <span style={styles.sectionMeta}>
              <BarChart3 size={16} />
              {t.showingOf(filtered.length, departments.length)}
            </span>
          )}
        </div>

        <div style={styles.toolbar}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}><Search size={14} />{t.search}</label>
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder={t.searchPlaceholder}
              theme={theme}
              language={language}
              icon={true}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}><Briefcase size={14} />{t.company}</label>
            <ReusableDropdown
              items={[{ id: ALL_COMPANIES, label: t.allCompanies }, ...companies.map((c) => ({ id: c, label: c }))]}
              value={selectedCompany}
              onChange={(value) => { setSelectedCompany(value); setSelectedIds(new Set()); }}
              placeholder={t.company}
              getItemKey={(item) => item.id}
              getItemLabel={(item) => item.label}
              searchable={companies.length > 7}
              theme={theme}
              language={language}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}><Filter size={14} />{t.status}</label>
            <ReusableDropdown
              items={[
                { id: "active", label: t.activeOnly },
                { id: "all", label: t.includeInactive },
              ]}
              value={showInactive ? "all" : "active"}
              onChange={(value) => setShowInactive(value === "all")}
              placeholder={t.status}
              getItemKey={(item) => item.id}
              getItemLabel={(item) => item.label}
              searchable={false}
              theme={theme}
              language={language}
            />
          </div>
        </div>
      </div>

      {/* ── Departments list ── */}
      <div style={styles.sectionCard}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>
            <Building2 size={screenSize.isMobile ? 16 : 20} />
            {t.departmentsList}
          </h3>
          {selectedCompany && filtered.length > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <span style={styles.sectionMeta}>{t.departmentsCount(filtered.length)}</span>
              {can.merge && selectedCount > 0 && (
                <>
                  <span style={styles.chip}>{t.selectedCount(selectedCount)}</span>
                  <button
                    type="button"
                    className="dept-icon-btn"
                    style={{ ...styles.iconBtn(), width: "28px", height: "28px" }}
                    title={t.clearSelection}
                    aria-label={t.clearSelection}
                    onClick={() => setSelectedIds(new Set())}
                  >
                    <X size={14} />
                  </button>
                </>
              )}
            </span>
          )}
        </div>

        {!selectedCompany ? (
          <div style={styles.emptyState}>
            <Building2 size={44} style={styles.emptyIcon} />
            <p style={styles.emptyTitle}>{t.selectCompanyFirst}</p>
          </div>
        ) : loading && !departments.length ? (
          renderSkeletonRows()
        ) : !filtered.length ? (
          <div style={{ ...styles.tableWrap, ...styles.emptyState }}>
            {hasSearch ? <SearchX size={44} style={styles.emptyIcon} /> : <Building2 size={44} style={styles.emptyIcon} />}
            <p style={styles.emptyTitle}>{hasSearch ? t.emptySearch : t.empty}</p>
            {!hasSearch && can.add && <p style={{ ...styles.hint, margin: 0 }}>{t.emptyHint}</p>}
          </div>
        ) : (
          <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s ease" }}>
            <DepartmentsTable
              styles={styles}
              t={t}
              language={language}
              can={can}
              departments={filtered}
              selectedIds={selectedIds}
              isMobile={screenSize.isMobile}
              showCompany={isAllCompanies}
              onToggleSelect={toggleSelect}
              onViewMembers={openMembers}
              onEdit={(d) => setEditModal({ mode: "edit", dept: d, name: d.name, nameAr: d.nameAr || "", description: d.description || "" })}
              onManagers={openManagers}
              onDeactivate={setConfirmDeactivate}
            />
          </div>
        )}
      </div>

      {editModal && (
        <DepartmentFormModal
          styles={styles}
          t={t}
          language={language}
          busy={busy}
          form={editModal}
          setForm={setEditModal}
          companies={companies}
          theme={theme}
          onClose={() => setEditModal(null)}
          onSave={() => run(async () => {
            const payload = { name: editModal.name, nameAr: editModal.nameAr || null, description: editModal.description || null };
            if (editModal.mode === "create" && editModal.workingCompany) payload.workingCompany = editModal.workingCompany;
            if (editModal.mode === "create") await createDepartment(payload);
            else await updateDepartment(editModal.dept.id, payload);
            setEditModal(null);
          })}
        />
      )}

      {mergeModal && (
        <MergeModal
          styles={styles}
          t={t}
          language={language}
          busy={busy}
          theme={theme}
          departments={departments}
          selectedIds={selectedIds}
          targetId={mergeTargetId}
          setTargetId={setMergeTargetId}
          onClose={() => setMergeModal(false)}
          onConfirm={() => run(async () => {
            const target = Number(mergeTargetId);
            const sources = [...selectedIds].filter((id) => id !== target);
            await mergeDepartments(target, sources);
            setSelectedIds(new Set());
            setMergeModal(false);
          })}
        />
      )}

      {membersModal && (
        <MembersModal
          styles={styles}
          t={t}
          language={language}
          state={membersModal}
          onClose={() => setMembersModal(null)}
        />
      )}

      {managersModal && (
        <ManagersModal
          styles={styles}
          t={t}
          language={language}
          busy={busy}
          theme={theme}
          state={managersModal}
          setState={setManagersModal}
          onClose={() => setManagersModal(null)}
          onSave={() => run(async () => {
            await setManagers(managersModal.dept.id, managersModal.list.filter((m) => m.infinityCode.trim()));
            setManagersModal(null);
          })}
        />
      )}

      {confirmDeactivate && (
        <DeactivateModal
          styles={styles}
          t={t}
          language={language}
          busy={busy}
          dept={confirmDeactivate}
          onClose={() => setConfirmDeactivate(null)}
          onConfirm={() => run(async () => {
            await deactivateDepartment(confirmDeactivate.id);
            setConfirmDeactivate(null);
          })}
        />
      )}

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onConfirm={() => setAlertModal({ ...alertModal, open: false })}
        onClose={() => setAlertModal({ ...alertModal, open: false })}
        theme={theme}
        language={language}
      />
    </div>
  );
}

export default DepartmentsPage;
